import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
if (!BACKEND_URL) {
    // Fail fast in dev if env is missing
    // eslint-disable-next-line no-console
    console.warn("REACT_APP_BACKEND_URL is not set");
}

export const API_BASE = `${BACKEND_URL || ""}/api`;

export const TOKEN_KEY = "offernest_token";
export const USER_KEY = "offernest_user";

export const api = axios.create({
    baseURL: API_BASE,
    headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
        config.headers = config.headers || {};
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// A single 401 handler that clears the session and bounces to /login.
// We expose a mutable ref so components can subscribe if they want; but the
// default is to hard-redirect.
let onUnauthorized = () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    if (window.location.pathname !== "/login") {
        window.location.href = "/login";
    }
};

export const setUnauthorizedHandler = (fn) => {
    if (typeof fn === "function") onUnauthorized = fn;
};

api.interceptors.response.use(
    (r) => r,
    (err) => {
        const status = err?.response?.status;
        if (status === 401) onUnauthorized();
        return Promise.reject(err);
    },
);

export const errorMessage = (err) => {
    const d = err?.response?.data?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join("; ");
    return err?.message || "Something went wrong";
};

// Convenience wrappers -------------------------------------------------------

export const uploadFile = async (file, purpose = "poster") => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("purpose", purpose);
    const res = await api.post("/uploads", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
};

export const uploadAndCreatePost = async (file, postPayload) => {
    const fd = new FormData();
    fd.append("file", file);
    fd.append("post", JSON.stringify(postPayload));
    const res = await api.post("/posts/upload-and-create", fd, {
        headers: { "Content-Type": "multipart/form-data" },
    });
    return res.data;
};

/** Fetch a URL that lives under the API host, returning a blob URL.
 * Used for images that require the Authorization header
 * (e.g. verification_doc uploads).
 */
export const fetchAuthedBlobUrl = async (path) => {
    const token = localStorage.getItem(TOKEN_KEY);
    const url = path.startsWith("http") ? path : `${BACKEND_URL || ""}${path}`;
    const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Fetch failed: ${res.status}`);
    const blob = await res.blob();
    return URL.createObjectURL(blob);
};

export const publicAssetUrl = (path) =>
    !path ? "" : path.startsWith("http") ? path : `${BACKEND_URL || ""}${path}`;
